import {
  Container,
  Skeleton,
  Stack,
  Typography,
  useTheme
} from '@rabex-kit/rabex-ui';
import { NavLink } from 'react-router-dom';
import HomeIcon from '../../static/images/svg/footer/Home.svg?react';
import MarketIcon from '../../static/images/svg/footer/Market.svg?react';
import TradeIcon from '../../static/images/svg/footer/Trade.svg?react';
import OtcIcon from '../../static/images/svg/footer/OTC.svg?react';
import WalletIcon from '../../static/images/svg/footer/Wallet.svg?react';
import Icon from '@/components/Icon';
import { MOBILE_FOOTER_HEIGHT } from '@/constants/layout';
import TokenManager from '@/utils/tokenManager';
import * as routes from '@/pages/routes';
import { P2pPageSkeleton } from '@/components/P2p/PageSkeleton';

/**
 * The tab bar, drawn here so it does not blink out while a tab loads.
 *
 * Not the real MainFooter: that one carries five Lottie clips, and this file
 * lives in the entry chunk. Same icons, same labels, same height — the swap
 * back to the animated bar happens under the finished page.
 */
const FOOTER_TABS = [
  { link: routes.DASHBOARD, text: 'خانه', icon: <HomeIcon /> },
  { link: routes.MARKETS, text: 'بازار‌ها', icon: <MarketIcon /> },
  { link: `${routes.P2P_STATIC}/USDTTMN`, text: 'معامله', icon: <TradeIcon /> },
  { link: routes.OTC, text: 'تبدیل', icon: <OtcIcon /> },
  { link: routes.WALLET, text: 'کیف‌پول', icon: <WalletIcon /> }
];

const SkeletonFooter = () => {
  const { palette } = useTheme();

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      justifyContent="space-evenly"
      height={MOBILE_FOOTER_HEIGHT}
      position="fixed"
      left={0}
      right={0}
      bottom={0}
      zIndex={1000}
      bgcolor={palette.base[50]}
      borderTop={`1px solid ${palette.base[200]}`}>
      {FOOTER_TABS.map((tab) => (
        <NavLink
          key={tab.link}
          to={tab.link}
          style={{ textDecoration: 'none' }}>
          <Stack alignItems="center" justifyContent="center" gap={1} width={64}>
            <Icon size={24} color={palette.textColor[400]}>
              {tab.icon}
            </Icon>
            <Typography variant="body4" color={palette.textColor[400]}>
              {tab.text}
            </Typography>
          </Stack>
        </NavLink>
      ))}
    </Stack>
  );
};

/** Which tab is loading; each one shimmers in its own shape. */
export type SkeletonVariant =
  | 'dashboard'
  | 'markets'
  | 'trade'
  | 'p2p'
  | 'convert'
  | 'wallet'
  | 'history'
  | 'gold'
  | 'rwa'
  | 'form'
  | 'generic';

/**
 * The frame a page draws around its content, which differs page to page:
 * the mobile bar on top (a title bar, a bare back arrow, or none because the
 * page draws its own), the bottom tab bar, and on desktop the sidebar beside
 * the content. Read off each page's DashboardLayout props.
 */
export type Chrome = {
  mobileBar: 'title' | 'back' | 'none';
  footer: boolean;
  sidebar: 'wallet' | 'profile' | null;
};

export const DEFAULT_CHROME: Chrome = {
  mobileBar: 'title',
  footer: true,
  sidebar: null
};

type PageSkeletonProps = { variant?: SkeletonVariant; chrome?: Chrome };

const SIDEBAR_WIDTH = { wallet: 232, profile: 200 } as const;

type Layout = 'mobile' | 'desktop';

const wave = { variant: 'rounded', animation: 'wave' } as const;

/** A card-shaped block on the page colour. */
const Block = ({ height, radius = 3 }: { height: number; radius?: number }) => (
  <Skeleton
    {...wave}
    width="100%"
    height={height}
    sx={{ borderRadius: radius }}
  />
);

/**
 * The OTC calculator's body while its markets load: tabs, the two amount
 * cards, the info rows and the action button. The route fallback and the page
 * itself both draw this, so the hand-off between them is one skeleton rather
 * than two different ones.
 */
export const ConvertCardSkeleton = () => (
  <Stack flex={1} gap={3} aria-busy="true">
    <Skeleton {...wave} height={40} />
    <Skeleton {...wave} height={104} />
    <Skeleton {...wave} height={104} />
    <Stack gap={2} mt="auto">
      <Skeleton {...wave} height={16} width="60%" />
      <Skeleton {...wave} height={16} width="45%" />
      <Skeleton {...wave} height={48} />
    </Stack>
  </Stack>
);

const DesktopConvertCard = () => {
  const { palette } = useTheme();
  return (
    <Stack
      width={470}
      maxWidth="100%"
      p={4}
      borderRadius={4}
      bgcolor={palette.base[50]}>
      <ConvertCardSkeleton />
    </Stack>
  );
};

/** A list row: mark, two lines of text, and a figure on the far side. */
const Row = () => {
  const { palette } = useTheme();

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      gap={3}
      py={3}
      borderBottom={`1px solid ${palette.base[200]}`}>
      <Skeleton variant="circular" animation="wave" width={32} height={32} />
      <Stack flex={1} gap={1}>
        <Skeleton {...wave} width="45%" height={14} />
        <Skeleton {...wave} width="25%" height={12} />
      </Stack>
      <Stack alignItems="flex-start" gap={1}>
        <Skeleton {...wave} width={72} height={14} />
        <Skeleton {...wave} width={44} height={12} />
      </Stack>
    </Stack>
  );
};

const List = ({ rows = 7 }: { rows?: number }) => (
  <Stack>
    {Array.from({ length: rows }, (_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Row key={index} />
    ))}
  </Stack>
);

/** Quick access: a scrolling track of round shortcuts under the balance. */
const Shortcuts = () => (
  <Stack flexDirection="row" gap={4} sx={{ overflow: 'hidden' }}>
    {Array.from({ length: 5 }, (_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Stack key={index} alignItems="center" gap={2} flexShrink={0}>
        <Skeleton variant="circular" animation="wave" width={48} height={48} />
        <Skeleton {...wave} width={48} height={10} />
      </Stack>
    ))}
  </Stack>
);

/** The back-arrow bar a page draws itself on mobile (history, portfolio). */
const BackBar = () => {
  const { palette } = useTheme();

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      gap={3}
      px={4}
      height={56}
      borderBottom={`1px solid ${palette.base[200]}`}>
      <Skeleton variant="circular" animation="wave" width={28} height={28} />
      <Skeleton {...wave} width={150} height={20} />
    </Stack>
  );
};

const variants: Record<SkeletonVariant, JSX.Element> = {
  // Search bar, balance, shortcuts, banner, then the market table.
  dashboard: (
    <>
      <Block height={140} />
      <Shortcuts />
      <Block height={96} />
      <List rows={6} />
    </>
  ),
  // The highlight card (winners / losers / newest / hot), then the table with
  // its tabs and search over the rows.
  markets: (
    <>
      <Block height={188} />
      <Stack flexDirection="row" justifyContent="center" gap={1}>
        <Skeleton {...wave} width={30} height={8} sx={{ borderRadius: 4 }} />
        <Skeleton {...wave} width={8} height={8} sx={{ borderRadius: 4 }} />
        <Skeleton {...wave} width={8} height={8} sx={{ borderRadius: 4 }} />
      </Stack>
      <Stack flexDirection="row" gap={2}>
        <Skeleton {...wave} width="40%" height={36} sx={{ borderRadius: 2 }} />
        <Skeleton {...wave} width="25%" height={36} sx={{ borderRadius: 2 }} />
      </Stack>
      <List rows={8} />
    </>
  ),
  // The p2p page draws its own full-width frame (see P2pPageSkeleton).
  p2p: <P2pPageSkeleton />,
  // Pair header, chart, then the order book and the form beneath it.
  trade: (
    <>
      <Block height={56} />
      <Block height={220} />
      <Block height={180} />
    </>
  ),
  // Two amount cards with the swap button between them.
  convert: <ConvertCardSkeleton />,
  // Balance card, the action tiles, then the asset list.
  wallet: (
    <>
      <Block height={180} />
      <Stack flexDirection="row" gap={3}>
        {Array.from({ length: 4 }, (_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Skeleton
            key={index}
            {...wave}
            width="100%"
            height={64}
            sx={{ borderRadius: 2, flex: 1 }}
          />
        ))}
      </Stack>
      <List rows={6} />
    </>
  ),
  // Filter chips over the transaction rows.
  history: (
    <>
      <Stack flexDirection="row" gap={2}>
        <Skeleton {...wave} width={80} height={32} sx={{ borderRadius: 4 }} />
        <Skeleton {...wave} width={80} height={32} sx={{ borderRadius: 4 }} />
        <Skeleton {...wave} width={80} height={32} sx={{ borderRadius: 4 }} />
      </Stack>
      <List rows={9} />
    </>
  ),
  // Live price, chart, then the buy/sell form.
  gold: (
    <>
      <Block height={96} />
      <Block height={200} />
      <Block height={56} radius={2} />
      <Block height={140} />
    </>
  ),
  // Holdings summary over the asset rows.
  rwa: (
    <>
      <Block height={140} />
      <List rows={6} />
    </>
  ),
  // Deposit / withdraw: method tabs, the field card, the action button.
  form: (
    <>
      <Block height={44} radius={2} />
      <Block height={200} />
      <Block height={48} radius={2} />
    </>
  ),
  generic: (
    <>
      <Block height={180} />
      <Block height={160} />
      <Block height={220} />
    </>
  )
};

/**
 * Stand-in for a tab that is still loading, once the app itself is up: the
 * shape of the page being opened rather than the brand splash, which would
 * read as the app restarting. Narrow layout only — see RouteFallback.
 */
const PageSkeleton = ({
  variant = 'generic',
  chrome = DEFAULT_CHROME
}: PageSkeletonProps) => {
  const { palette } = useTheme();

  return (
    <Stack
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="در حال بارگذاری"
      width="100%"
      minHeight="100vh"
      bgcolor={palette.base[50]}>
      {/* The bar the mobile header leaves at the top of most tabs */}
      {chrome.mobileBar === 'back' && <BackBar />}
      {chrome.mobileBar === 'title' && (
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          px={4}
          height={56}>
          <Skeleton {...wave} width={110} height={24} />
          <Stack flexDirection="row" gap={2}>
            <Skeleton
              variant="circular"
              animation="wave"
              width={28}
              height={28}
            />
            <Skeleton
              variant="circular"
              animation="wave"
              width={28}
              height={28}
            />
          </Stack>
        </Stack>
      )}

      <Stack
        px={4}
        py={3}
        gap={3}
        pb={`${chrome.footer ? MOBILE_FOOTER_HEIGHT + 16 : 16}px`}>
        {variants[variant]}
      </Stack>

      {chrome.footer && <SkeletonFooter />}
    </Stack>
  );
};

export default PageSkeleton;

// ─────────────────────────────────────────────────────────────────────────────
// Wide layout
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The top bar, in the proportions of the real DesktopHeader: logo, then the
 * five menu entries and the gold pill; at the far end the download button and,
 * for a guest, the two sign-in buttons — for a member the bell, wallet and
 * avatar. Whether there is a session is known synchronously, so the skeleton
 * does not guess wrong and swap clusters once the page lands.
 */
const NAV_WIDTHS = [64, 56, 84, 72, 44];

const DesktopHeaderSkeleton = () => {
  const { palette } = useTheme();
  const isMember = TokenManager.authenticated();

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      py={3}
      px={{ lg: 3, xl: 6 }}
      bgcolor={palette.base[50]}>
      <Stack flexDirection="row" alignItems="center">
        <Skeleton {...wave} width={108} height={40} sx={{ ml: 3 }} />
        <Stack flexDirection="row" alignItems="center" gap={0.5}>
          {NAV_WIDTHS.map((width, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Stack key={index} height={40} justifyContent="center" px={3}>
              <Skeleton {...wave} width={width} height={14} />
            </Stack>
          ))}
          <Skeleton
            {...wave}
            width={104}
            height={36}
            sx={{ borderRadius: 5 }}
          />
        </Stack>
      </Stack>
      <Stack flexDirection="row" alignItems="center" gap={{ lg: 2, xl: 4 }}>
        {isMember ? (
          <>
            <Skeleton
              variant="circular"
              animation="wave"
              width={40}
              height={40}
            />
            <Skeleton
              {...wave}
              width={140}
              height={40}
              sx={{ borderRadius: 2 }}
            />
            <Skeleton
              variant="circular"
              animation="wave"
              width={40}
              height={40}
            />
          </>
        ) : (
          <>
            <Skeleton
              {...wave}
              width={72}
              height={40}
              sx={{ borderRadius: 2 }}
            />
            <Skeleton
              {...wave}
              width={100}
              height={40}
              sx={{ borderRadius: 2 }}
            />
          </>
        )}
        <Skeleton variant="circular" animation="wave" width={40} height={40} />
      </Stack>
    </Stack>
  );
};

const Columns = ({ children }: { children: JSX.Element[] }) => (
  <Stack flexDirection="row" gap={4} width="100%">
    {children}
  </Stack>
);

const desktopVariants: Record<SkeletonVariant, JSX.Element> = {
  // Shortcuts and banners over a balance + tier pair, then the market table.
  dashboard: (
    <>
      <Shortcuts />
      <Block height={140} />
      <Columns>
        <Block height={200} />
        <Block height={200} />
      </Columns>
      <List rows={6} />
    </>
  ),
  // Page title, the four highlight boxes, then the table.
  markets: (
    <>
      <Skeleton {...wave} width={180} height={24} />
      <Columns>
        <Block height={220} />
        <Block height={220} />
        <Block height={220} />
        <Block height={220} />
      </Columns>
      <Stack flexDirection="row" gap={2}>
        <Skeleton {...wave} width={220} height={40} sx={{ borderRadius: 2 }} />
        <Skeleton {...wave} width={140} height={40} sx={{ borderRadius: 2 }} />
      </Stack>
      <List rows={10} />
    </>
  ),
  p2p: <P2pPageSkeleton />,
  // Market bar over the three columns: order book, chart, trade form.
  trade: (
    <>
      <Block height={64} />
      <Columns>
        <Block height={460} />
        <Block height={460} />
        <Block height={460} />
      </Columns>
    </>
  ),
  // Same card the page draws (470 wide, 16px padding) so nothing jumps.
  convert: (
    <Stack alignItems="center">
      <DesktopConvertCard />
    </Stack>
  ),
  // Asset sidebar beside the balance card and the asset list.
  wallet: (
    <>
      <Block height={180} />
      <List rows={6} />
    </>
  ),
  // The wallet sidebar beside a card holding the filters and the rows.
  history: (
    <>
      <Stack flexDirection="row" gap={2}>
        <Skeleton {...wave} width={96} height={36} sx={{ borderRadius: 4 }} />
        <Skeleton {...wave} width={96} height={36} sx={{ borderRadius: 4 }} />
        <Skeleton {...wave} width={96} height={36} sx={{ borderRadius: 4 }} />
      </Stack>
      <List rows={10} />
    </>
  ),
  // Price and chart beside the buy/sell form.
  gold: (
    <>
      <Block height={96} />
      <Columns>
        <Block height={420} />
        <Stack width={380} flexShrink={0}>
          <Block height={420} />
        </Stack>
      </Columns>
    </>
  ),
  rwa: (
    <>
      <Columns>
        <Block height={140} />
        <Block height={140} />
        <Block height={140} />
      </Columns>
      <List rows={6} />
    </>
  ),
  form: (
    <Stack alignItems="center">
      <Stack width={480} maxWidth="100%" gap={3}>
        <Block height={44} radius={2} />
        <Block height={240} />
        <Block height={48} radius={2} />
      </Stack>
    </Stack>
  ),
  generic: (
    <>
      <Block height={120} />
      <Columns>
        <Block height={260} />
        <Block height={260} />
      </Columns>
    </>
  )
};

/** Wide-layout stand-in: the header bar and the page's body, no footer. */
const DesktopPageSkeleton = ({
  variant = 'generic',
  chrome = DEFAULT_CHROME
}: PageSkeletonProps) => {
  const { palette } = useTheme();

  return (
    <Stack
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="در حال بارگذاری"
      width="100%"
      minHeight="100vh"
      bgcolor={palette.base[200]}>
      <DesktopHeaderSkeleton />
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {chrome.sidebar ? (
          <Stack flexDirection="row" gap={4}>
            <Stack width={SIDEBAR_WIDTH[chrome.sidebar]} flexShrink={0}>
              <Block height={420} />
            </Stack>
            <Stack flex={1} minWidth={0} gap={4}>
              {desktopVariants[variant]}
            </Stack>
          </Stack>
        ) : (
          <Stack gap={4}>{desktopVariants[variant]}</Stack>
        )}
      </Container>
    </Stack>
  );
};

export const ResponsivePageSkeleton = ({
  layout,
  variant,
  chrome
}: PageSkeletonProps & { layout: Layout }) =>
  layout === 'desktop' ? (
    <DesktopPageSkeleton variant={variant} chrome={chrome} />
  ) : (
    <PageSkeleton variant={variant} chrome={chrome} />
  );

/**
 * Just the page body, for a frame that is already on screen (see WalletShell):
 * the header, sidebar and tab bar are the real ones, so only what sits inside
 * them is a placeholder. A page that draws its own top bar on mobile still
 * gets that bar here.
 */
export const ContentSkeleton = ({
  layout,
  variant = 'generic',
  chrome = DEFAULT_CHROME
}: PageSkeletonProps & { layout: Layout }) => {
  const { palette } = useTheme();

  // Drawn exactly like the page's own loading state, with no extra padding,
  // so the page takes over without the shimmer changing shape.
  if (variant === 'p2p') return <P2pPageSkeleton />;

  if (layout === 'desktop') {
    return (
      <Stack
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="در حال بارگذاری"
        gap={4}>
        {desktopVariants[variant]}
      </Stack>
    );
  }

  return (
    <Stack
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="در حال بارگذاری"
      bgcolor={palette.base[50]}>
      {chrome.mobileBar === 'back' && <BackBar />}
      <Stack px={4} py={3} gap={3}>
        {variants[variant]}
      </Stack>
    </Stack>
  );
};
